/**
 * Development Summary Component
 *
 * Displays development summary information to help developers
 * re-engage with the project and understand current priorities.
 */

import React, { useState, useEffect } from 'react';
import { Card, List, Typography, Alert, Spin, Tag, Progress, Space, Button } from 'antd';
import {
  RocketOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  FileTextOutlined,
  ApiOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { developmentService, DevelopmentSummary } from '../../services/api/developmentService';

const { Title, Text } = Typography;

interface DevelopmentSummaryProps {
  className?: string;
}

export const DevelopmentSummaryComponent: React.FC<DevelopmentSummaryProps> = ({ className }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<DevelopmentSummary | null>(null);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await developmentService.getDevelopmentSummary();
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch development summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSummary();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready':
        return <CheckCircleOutlined />;
      case 'warning':
        return <ExclamationCircleOutlined />;
      case 'error':
        return <ExclamationCircleOutlined />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  if (loading) {
    return (
      <div className={className} style={{ textAlign: 'center', padding: '2rem' }}>
        <Spin size="large" />
        <div style={{ marginTop: '1rem' }}>
          <Text>Loading development summary...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={className}>
        <Alert
          message="Failed to Load Development Summary"
          description={error}
          type="error"
          action={
            <Button size="small" danger onClick={fetchSummary}>
              <ReloadOutlined /> Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className={className}>
        <Alert
          message="No Development Summary Available"
          description="Unable to load development summary information."
          type="warning"
        />
      </div>
    );
  }

  const { project_status, implementation_plan, quick_links, development_tips } = summary;

  return (
    <div className={className}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Card>
          <Space align="center">
            <RocketOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
            <div>
              <Title level={2} style={{ margin: 0 }}>
                {project_status.project_name} Development Summary
              </Title>
              <Text type="secondary">
                Version {project_status.version} • {project_status.environment} environment
              </Text>
            </div>
          </Space>
        </Card>

        {/* Project Status */}
        <Card title="Project Status" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Backend: </Text>
              <Tag
                color={getStatusColor(project_status.backend_status.status)}
                icon={getStatusIcon(project_status.backend_status.status)}
              >
                {project_status.backend_status.status}
              </Tag>
              <Text type="secondary"> - {project_status.backend_status.message}</Text>
            </div>
            <div>
              <Text strong>Frontend: </Text>
              <Tag
                color={getStatusColor(project_status.frontend_status.status)}
                icon={getStatusIcon(project_status.frontend_status.status)}
              >
                {project_status.frontend_status.status}
              </Tag>
              <Text type="secondary"> - {project_status.frontend_status.message}</Text>
            </div>
            <div>
              <Text strong>Database: </Text>
              <Tag
                color={getStatusColor(project_status.database_status.status)}
                icon={getStatusIcon(project_status.database_status.status)}
              >
                {project_status.database_status.status}
              </Tag>
              <Text type="secondary"> - {project_status.database_status.message}</Text>
            </div>
          </Space>
        </Card>

        {/* Implementation Plan Summary */}
        {implementation_plan.available && (
          <Card title="Implementation Plan Summary" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              {implementation_plan.current_phase && (
                <div>
                  <Text strong>Current Phase: </Text>
                  <Text>{implementation_plan.current_phase}</Text>
                </div>
              )}

              {implementation_plan.completion_status && (
                <div>
                  <Text strong>Progress: </Text>
                  <Progress
                    percent={implementation_plan.completion_status.completion_percentage}
                    size="small"
                    style={{ width: '200px', display: 'inline-block', marginLeft: '8px' }}
                  />
                  <Text type="secondary" style={{ marginLeft: '8px' }}>
                    {implementation_plan.completion_status.completed_items} /{' '}
                    {implementation_plan.completion_status.total_items} items
                  </Text>
                </div>
              )}

              {implementation_plan.next_priority_items &&
                implementation_plan.next_priority_items.length > 0 && (
                  <div>
                    <Text strong>Next Priority Items:</Text>
                    <List
                      size="small"
                      dataSource={implementation_plan.next_priority_items}
                      renderItem={(item, index) => (
                        <List.Item>
                          <Text>
                            {index + 1}. {item}
                          </Text>
                        </List.Item>
                      )}
                    />
                  </div>
                )}

              {implementation_plan.immediate_issues &&
                implementation_plan.immediate_issues.length > 0 && (
                  <div>
                    <Text strong>Immediate Issues:</Text>
                    <List
                      size="small"
                      dataSource={implementation_plan.immediate_issues}
                      renderItem={(item, index) => (
                        <List.Item>
                          <Text type="warning">
                            {index + 1}. {item}
                          </Text>
                        </List.Item>
                      )}
                    />
                  </div>
                )}
            </Space>
          </Card>
        )}

        {/* Quick Links */}
        <Card title="Quick Links" size="small">
          <Space wrap>
            <Button
              type="link"
              icon={<ApiOutlined />}
              href={quick_links.backend_api}
              target="_blank"
            >
              Backend API
            </Button>
            <Button
              type="link"
              icon={<GlobalOutlined />}
              href={quick_links.frontend_app}
              target="_blank"
            >
              Frontend App
            </Button>
            <Button
              type="link"
              icon={<FileTextOutlined />}
              href={quick_links.api_docs}
              target="_blank"
            >
              API Documentation
            </Button>
          </Space>
        </Card>

        {/* Development Tips */}
        <Card title="Development Tips" size="small">
          <List
            size="small"
            dataSource={development_tips}
            renderItem={(tip) => (
              <List.Item>
                <Text type="secondary">💡 {tip}</Text>
              </List.Item>
            )}
          />
        </Card>

        {/* Refresh Button */}
        <div style={{ textAlign: 'center' }}>
          <Button type="primary" icon={<ReloadOutlined />} onClick={fetchSummary} loading={loading}>
            Refresh Summary
          </Button>
        </div>
      </Space>
    </div>
  );
};

export default DevelopmentSummaryComponent;
